import { DATE_FORMAT } from "@/constants/datetime";
import { INVALID_SELECT_NOT_EMPTY } from "@/constants/invalids";
import { PATTERN } from "@/constants/pattern";
import { ERSOContractStatus, ERsoOrganizationType } from "@/types/enum";
import { AUTO_CONVERT_FULL_WIDTH_CHAR_TYPES } from "@/utils/checkCharTypeValidator";
import { dayjs } from "@/utils/dayjs";
import { replaceStr } from "@/utils/string";
import { validator } from "@/utils/validator";

export const registerSupportOrganizationSchema = validator.object().shape({
  // Message pinned per-rule: .isSelection() only chains on string/number/mixed, not array.
  organizationType: validator
    .array()
    .of(validator.string().required())
    .label("種別")
    .required(({ label }) => replaceStr(INVALID_SELECT_NOT_EMPTY, { path: label }))
    .min(1, ({ label }) => replaceStr(INVALID_SELECT_NOT_EMPTY, { path: label })),
  rsoName: validator
    .string()
    .trim()
    .emptyToNull()
    .max(60)
    .autoTransformToFullWidth()
    .isValidCheckCharType(AUTO_CONVERT_FULL_WIDTH_CHAR_TYPES)
    .label("名称")
    .required(),
  // 登録番号 is only mandatory when 種別 includes 登録支援機関. Callback form of .when() is required
  // here because organizationType is an array — the `is:` shorthand can't express `.includes()`.
  // Format check still applies to any entered value, even when the field is optional.
  rsoRegistrationNumber: validator
    .string()
    .trim()
    .emptyToNull()
    .label("登録番号")
    // The create form seeds this field with a bare `YY登-` prefix, which is not a real number. Treat
    // it as empty so an optional 登録番号 doesn't fail the format check on an untouched form.
    .transform((value) => (typeof value === "string" && PATTERN.RSO_REGISTRATION_NUMBER_PREFIX_ONLY.test(value.trim()) ? null : value))
    // Format is checked via .test() rather than .matches() so an empty/optional value is skipped
    // (yup runs .matches() on null once the field is nullable).
    .test("rso-registration-number-format", "無効な登録番号の形式です。期待される形式: YY登-XXXXXX", (value) => {
      if (!value) return true;
      return PATTERN.RSO_REGISTRATION_NUMBER.test(value);
    })
    .when("organizationType", ([organizationType], schema) => {
      const isRegisteredSupport = Array.isArray(organizationType) && organizationType.includes(ERsoOrganizationType.REGISTERED_SUPPORT);
      return isRegisteredSupport ? schema.required() : schema.notRequired().nullable().default(null);
    })
    .nullable()
    .defined(),
  contractStatus: validator
    .string()
    .isSelection()
    .emptyToNull()
    .label("ステータス")
    .test((value, ctx) => {
      const todayStr = dayjs().format(DATE_FORMAT);
      const trialEndDateStr = ctx.parent.trialEndDate ? dayjs(ctx.parent.trialEndDate).format(DATE_FORMAT) : null;
      const contractEndDateStr = ctx.parent.contractEndDate ? dayjs(ctx.parent.contractEndDate).format(DATE_FORMAT) : null;
      const contractStartDateStr = ctx.parent.contractStartDate ? dayjs(ctx.parent.contractStartDate).format(DATE_FORMAT) : null;
      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ERSOContractStatus.TRIAL &&
        trialEndDateStr &&
        dayjs(todayStr).isAfter(dayjs(trialEndDateStr))
      ) {
        return ctx.createError({ message: "トライアル終了日が過ぎているため、ステータスを「トライアル」に設定することはできません。" });
      }
      if (
        ctx.parent.contractStatus &&
        [ERSOContractStatus.TRIAL, ERSOContractStatus.DURING_CONTRACT].includes(ctx.parent.contractStatus) &&
        contractEndDateStr &&
        dayjs(todayStr).isAfter(dayjs(contractEndDateStr))
      ) {
        return ctx.createError({ message: "解約日を過ぎているため、ステータスを「トライアル」または「契約中」に設定することはできません。" });
      }
      if (
        !ctx.parent.trialEndDate &&
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ERSOContractStatus.TRIAL &&
        contractStartDateStr &&
        dayjs(todayStr).isSameOrAfter(contractStartDateStr)
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル」ステータスは選択できません。" });
      }
      if (
        ctx.parent.contractStatus &&
        ctx.parent.contractStatus === ERSOContractStatus.TRIAL_CANCELLED &&
        contractStartDateStr &&
        dayjs(contractStartDateStr).isSameOrBefore(dayjs(todayStr))
      ) {
        return ctx.createError({ message: "契約開始日が本日または過去の日付の場合、「トライアル解約済み」ステータスは選択できません。" });
      }
      return true;
    })
    .required(),
  contractPlan: validator.string().trim().emptyToNull().label("契約プラン").max(255).notRequired(),
  trialStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("トライアル開始日")
    .isBeforeDate("trialEndDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .notRequired(),
  trialEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "トライアル終了日" })
    .isSameOrBeforeDate("contractStartDate", { startLabel: "トライアル終了日", endLabel: "契約開始日" })
    .label("トライアル終了日")
    .notRequired(),
  contractStartDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("契約開始日")
    .isSameOrAfterDate("trialEndDate", { startLabel: "トライアル終了日", endLabel: "契約開始日" })
    .isAfterDate("trialStartDate", { startLabel: "トライアル開始日", endLabel: "契約開始日" })
    .when("contractStatus", {
      is: ERSOContractStatus.DURING_CONTRACT,
      then: (schema) => schema.required(),
    })
    .notRequired(),
  contractEndDate: validator
    .mixed()
    .isSelection()
    .isDayjs()
    .label("解約日")
    .notRequired()
    .test((value, ctx) => {
      const trialEndDateStr = ctx.parent.trialEndDate ? dayjs(ctx.parent.trialEndDate).format(DATE_FORMAT) : null;
      const contractEndDateStr = ctx.parent.contractEndDate ? dayjs(ctx.parent.contractEndDate).format(DATE_FORMAT) : null;
      const contractStartDateStr = ctx.parent.contractStartDate ? dayjs(ctx.parent.contractStartDate).format(DATE_FORMAT) : null;
      if (contractEndDateStr && contractStartDateStr && dayjs(contractStartDateStr).isSameOrAfter(dayjs(contractEndDateStr))) {
        return ctx.createError({ message: "「解約日」は「契約開始日」より後の日付を指定してください。" });
      }
      if (contractEndDateStr && trialEndDateStr && dayjs(trialEndDateStr).isSameOrAfter(dayjs(contractEndDateStr))) {
        return ctx.createError({ message: "「解約日」は「トライアル終了日」より後の日付を指定してください。" });
      }
      return true;
    }),
  startReportYear: validator.mixed().isSelection().isDayjs().label("定期届出の開始年度").required(),
  description: validator.string().trim().emptyToNull().label("登録支援機関情報").notRequired().max(255),
  allowedIps: validator
    .string()
    .trim()
    .emptyToNull()
    .label("許可するIPアドレス")
    .notRequired()
    .max(1000)
    .test("ip-whitelist", (value, ctx) => {
      if (!value || value.trim() === "") return true;

      const ipLines = value
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      for (let i = 0; i < ipLines.length; i++) {
        const ip = ipLines[i];
        // IP only (192.168.1.1)
        const ipRegex = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
        // CIDR (10.0.0.0/24)
        const cidrRegex = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)\/(3[0-2]|[12]?\d)$/;

        if (!ipRegex.test(ip) && !cidrRegex.test(ip)) {
          return ctx.createError({
            message: `${i + 1}行目: 許可するIPアドレス（${ip}）の形式が正しくありません。`,
          });
        }
      }
      return true;
    }),
  note: validator.string().trim().emptyToNull().label("備考").notRequired().max(255),
});
